import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader2, Utensils, Flame, Wheat, Beef, Cookie } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import { useCachedImage } from "@/hooks/use-cached-image";
import { RealtimePostgresChangesPayload, User } from '@supabase/supabase-js';
import { Skeleton } from "@/components/ui/skeleton";

type FoodEntry = {
  id?: string;
  hero_image_path: string;
  entry_name: string | null;
  consumed_at: string;
  analysis_status: string;
  total_quantity_g: number | null;
  total_calories: number | null;
  total_carbs: number | null;
  total_protein: number | null;
  total_fat: number | null;
};

// Separate component for entry card to reduce complexity
const EntryCard = ({ entry }: { entry: FoodEntry }) => {
  const navigate = useNavigate();
  const { imageUrl, isLoading: imageLoading, error: imageError } = useCachedImage(entry.hero_image_path);

  const renderImage = () => {
    if (!entry.hero_image_path) {
      return (
        <div className="bg-gray-100 h-64 rounded-lg flex items-center justify-center text-gray-500">
          <Utensils className="h-12 w-12" />
        </div>
      );
    }

    if (imageLoading) {
      return <div className="animate-pulse bg-gray-200 h-64 rounded-lg" />;
    }

    if (imageError || !imageUrl) {
      return <div className="bg-gray-100 h-64 rounded-lg flex items-center justify-center text-gray-500">Failed to load image</div>;
    }

    return (
      <img
        src={imageUrl}
        alt="Food"
        className="w-full h-64 object-cover"
        loading="lazy"
      />
    );
  };

  return (
    <Card 
      className="overflow-hidden animate-fadeIn cursor-pointer hover:shadow-lg transition-shadow"
      onClick={() => entry.id && navigate(`/entry/${entry.id}`)}
    >
      {renderImage()}
      <CardContent className="p-4">
        {entry.analysis_status === 'completed' ? (
          <>
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">{entry.entry_name || 'Unnamed Entry'}</h3>
              <p className="text-sm text-muted-foreground">
                {format(new Date(entry.consumed_at), 'PPpp')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 bg-orange-50 p-2 rounded-lg">
                <Flame className="h-4 w-4 text-orange-500" />
                <div>
                  <p className="text-sm font-medium">{Math.round(entry.total_calories || 0)}</p>
                  <p className="text-xs text-muted-foreground">calories</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-yellow-50 p-2 rounded-lg">
                <Wheat className="h-4 w-4 text-yellow-600" />
                <div>
                  <p className="text-sm font-medium">{Math.round(entry.total_carbs || 0)}g</p>
                  <p className="text-xs text-muted-foreground">carbs</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-red-50 p-2 rounded-lg">
                <Beef className="h-4 w-4 text-red-500" />
                <div>
                  <p className="text-sm font-medium">{Math.round(entry.total_protein || 0)}g</p>
                  <p className="text-xs text-muted-foreground">protein</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-blue-50 p-2 rounded-lg">
                <Cookie className="h-4 w-4 text-blue-500" />
                <div>
                  <p className="text-sm font-medium">{Math.round(entry.total_fat || 0)}g</p>
                  <p className="text-xs text-muted-foreground">fat</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>
              {entry.analysis_status === 'extracting' ? 'Extracting ingredients...' :
               entry.analysis_status === 'calculating' ? 'Calculating nutrition data...' :
               'Processing...'}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ITEMS_PER_PAGE = 6;

const fetchEntries = async (page: number, user: User | null) => {
  const { data: entries, error: entriesError, count } = await supabase
    .from('v_food_entries_aggregated')
    .select('*', { count: 'exact' })
    .eq('user_id', user?.id)
    .order('consumed_at', { ascending: false })
    .range(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE - 1);

  if (entriesError) throw entriesError;

  const mappedEntries = (entries as any[]).map(entry => ({
    ...entry,
    id: entry.entry_id,
    consumed_at: entry.consumed_at
  }));

  return {
    items: mappedEntries,
    totalCount: count || 0
  };
};

export const EntryFeed = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [allItems, setAllItems] = useState<FoodEntry[]>([]);
  const [user, setUser] = useState<User | null>(null);
  
  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    getUser();
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['entries'],
    queryFn: () => fetchEntries(page, user),
    enabled: !!user,
    staleTime: 0,
    gcTime: 0
  });

  useEffect(() => {
    if (data) {
      if (page === 0) {
        setAllItems(data.items);
      } else {
        setAllItems(prev => [...prev, ...data.items]);
      }
    }
  }, [data, page]);

  const hasMore = data ? allItems.length < data.totalCount : false;

  useEffect(() => {
    const entriesChannel = supabase
      .channel('food_entries_changes')
      .on(
        'postgres_changes' as const,
        { 
          event: '*', 
          schema: 'public', 
          table: 'food_entries',
          filter: `user_id=eq.${user?.id}` // Only listen for current user's entries
        },
        async (payload: RealtimePostgresChangesPayload<{new: { id: string }}>) => {
          // Fetch just the updated entry
          const { data: updatedEntry } = await supabase
            .from('v_food_entries_aggregated')
            .select('*')
            .eq('entry_id', payload.new.id)
            .eq('user_id', user?.id)
            .single();

          if (updatedEntry) {
            // Update the entry in the existing items array
            setAllItems(prev => prev.map(item => {
              if (item.id === updatedEntry.entry_id) {
                return {
                  ...updatedEntry,
                  id: updatedEntry.entry_id,
                  consumed_at: updatedEntry.consumed_at,
                  total_quantity_g: null,
                } as FoodEntry;
              }
              return item;
            }));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(entriesChannel);
    };
  }, [queryClient]);

  if (isLoading && page === 0) {
    return (
      <div className="w-full max-w-6xl mx-auto mt-12">
        <Skeleton className="h-10 w-48 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(ITEMS_PER_PAGE)].map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="h-64 w-full" />
              <CardContent className="p-4">
                <div className="space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <div className="grid grid-cols-2 gap-3">
                    {[...Array(4)].map((_, j) => (
                      <Skeleton key={j} className="h-12 w-full rounded-lg" />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center mt-8 text-red-500">Error loading entries</div>;
  }

  return (
    <div className="w-full max-w-6xl mx-auto mt-12">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">Recent entries</h2>
      {!allItems.length ? (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <p className="text-xl font-medium mb-2">You look hungry!</p>
          <p className="text-gray-600">Upload a photo of your next meal to get started</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allItems.map((entry) => (
              <EntryCard key={entry.id} entry={entry} />
            ))}
          </div>
          {hasMore && (
            <div className="mt-8 text-center">
              <Button
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};